<template>
  <info-action-button
    v-if="visible"
    :label="label"
    :icon="icon"
    :color="color"
    :border-color="borderColor"
    :progress="summary.status === 'downloading' ? summary.progress : null"
    :bar-color="$theme.primaryColor"
    :disabled="disabled"
    :title="tooltip"
    :width="width"
    @click="onClick"
  />
</template>

<script>
import InfoActionButton
  from "@/components/InfoActionButton";
import downloadActions
  from "@/mixins/downloadActions";

// Botão de baixar o CURSO. Vive em dois lugares — o cabeçalho do menu lateral e
// a página do curso — e por isso é componente, não markup repetido: os dois
// leem o mesmo agregado e passam pelo mesmo gesto, então não têm como divergir.
//
// Rótulo escrito, não só ícone: no cabeçalho do curso ele fica ao lado de
// módulos que também têm ícone de download, e sem a palavra o aluno não sabe
// qual escopo está tocando.
export default {
  name: 'CourseDownloadButton',
  components: { InfoActionButton },
  mixins: [downloadActions],
  props: {
    // O enroll inteiro, não a lista de turmas: as duas telas que usam este botão
    // teriam de repetir a mesma varredura das quatro estruturas possíveis, e a
    // primeira divergência entre elas seria silenciosa.
    enroll: { type: Object, default: null },
    width: { type: [Number, String], default: 150 }
  },
  computed: {
    enrollId () {
      return this.enroll && this.enroll.id;
    },
    courseId () {
      return this.enroll && this.enroll.courseId;
    },
    courseName () {
      return this.enroll && this.enroll.name;
    },
    // Achata classes, modules[].classes, modules[].disciplines[].classes e
    // disciplines[].classes — as quatro organizações que o grid pode ter.
    classes () {
      const e = this.enroll;
      if (!e) return [];
      const out = [];
      out.push(...(e.classes || []));
      for (const mod of (e.modules || [])) {
        out.push(...(mod.classes || []));
        for (const disc of (mod.disciplines || [])) out.push(...(disc.classes || []));
      }
      for (const disc of (e.disciplines || [])) out.push(...(disc.classes || []));
      return out;
    },
    // Só as aulas que o gesto de fato baixaria — mesmo critério do ícone por
    // linha no menu. Sem esse filtro o "0 de N" contaria quiz e link externo, e
    // o curso nunca chegaria a 100%.
    groups () {
      return (this.classes || [])
        .map(clss => {
          const los = (clss.learningObjects || []).filter(lo => this.loIsDownloadable(lo));
          return {
            id: clss.id,
            className: clss.title || clss.name || 'Módulo',
            learningObjectIds: los.map(lo => lo.id),
            hasVideo: los.some(lo => lo && lo.broadcast),
            done: los.length > 0 && los.every(lo => this.dlLoStatus(this.enrollId, lo.id) === 'downloaded')
          };
        })
        .filter(g => g.learningObjectIds.length > 0);
    },
    loIds () {
      return this.groups.reduce((acc, g) => acc.concat(g.learningObjectIds), []);
    },
    summary () {
      return this.dlEnrollSummary(this.enrollId, this.loIds);
    },
    visible () {
      return this.$isApp && !!this.enrollId && this.loIds.length > 0;
    },
    label () {
      switch (this.summary.status) {
        case 'downloaded': return 'Baixar conteúdo';
        // Aulas concluídas dizem mais que a fração do arquivo atual num download
        // que leva dezenas de minutos — e é o número que não anda pra trás.
        case 'downloading': return `${this.summary.done} de ${this.summary.total}`;
        default: return 'Baixar conteúdo';
      }
    },
    icon () {
      switch (this.summary.status) {
        case 'downloaded': return 'mobile_friendly';
        case 'downloading': return null;
        default: return 'save_alt';
      }
    },
    color () {
      return this.summary.status === 'idle' ? null : this.$theme.primaryColor;
    },
    borderColor () {
      return this.summary.status === 'downloaded' ? this.$theme.primaryColor : null;
    },
    // O toque agora SEMPRE abre a sheet; remover e cancelar viraram ações no
    // rodapé dela. O tooltip descreve o estado, não um atalho que não existe
    // mais.
    tooltip () {
      if (this.disabled) return 'Sem conexão — conecte-se para baixar';
      switch (this.summary.status) {
        case 'downloaded': return 'Curso disponível offline';
        case 'downloading': return 'Baixando';
        default: return 'Escolher o que baixar';
      }
    },
    // Mesma regra do botão da aula: offline impede COMEÇAR, mas cancelar e
    // remover continuam permitidos.
    disabled () {
      if (this.dlIsConnected) return false;
      return this.summary.status === 'idle';
    }
  },
  methods: {
    loIsDownloadable (lo) {
      if (!lo || lo.state === 'Unpublished' || lo.releasedDate) return false;
      return this.canDownloadLo(lo, this.courseId);
    },
    onClick () {
      if (this.disabled) return;
      this.toggleEnrollDownload({
        enrollId: this.enrollId,
        groups: this.groups,
        courseName: this.courseName,
        courseId: this.courseId,
        // > 0 acrescenta a aba "Materiais" na sheet — no app ela assume o papel
        // da entrada "Downloads" do resumo, escondida em ComposedHeader.
        filesCount: (this.enroll && this.enroll.summary && this.enroll.summary.filesAmount) || 0
      });
    }
  }
};
</script>
